import React, { useState, useRef, useEffect } from "react";
import { McModal, McButton, McTextarea } from "@maersk-global/mds-react-wrapper/components-core";

export default function ApproveModal({
  open,
  chargeId,
  parentNotice,
  parentNoticeName,
  userInfo,
  isBot = false,
  onClose,
  onSuccess,
  childId
}) {
  const modalRef = useRef(null);
  const prevOpenRef = useRef(false);
  const textareaRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" }); // info | success | error

  // Open/close the modal imperatively + reset UI on open
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;

    if (open && !prevOpenRef.current) {
      try {
        el.open = true;
      } catch {
        el.setAttribute("open", "");
      }

      // reset UI state
      setStatus({ type: null, message: "" });
      if (textareaRef.current) {
        textareaRef.current.value = "";
        setTimeout(() => textareaRef.current?.focus?.(), 0);
      }
    } else if (!open && prevOpenRef.current) {
      try {
        el.open = false;
      } catch {
        el.removeAttribute("open");
      }
    }

    prevOpenRef.current = open;
  }, [open]);

  // Confirm approval
  const handleConfirm = async () => {
    if (!chargeId) {
      setStatus({ type: "error", message: "Charge ID is missing." });
      return;
    }

    if (!userInfo) {
      setStatus({ type: "error", message: "User information not loaded. Please try again." });
      return;
    }

    const comments = textareaRef.current?.value?.trim() || "";
    const payload = {
      chargeNoticeId: chargeId,
      comments,
      actionType: "approve",
      userName: userInfo,
      isBot: isBot
    };

    setLoading(true);
    setStatus({ type: "info", message: "Submitting approval..." });

    try {
      const token = localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/approve-charge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}`,"consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY },
        body: JSON.stringify(payload)
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        // ignore parse errors
      }

      if (res.ok && data?.status === "success") {
        setStatus({ type: "success", message: "Charge approved successfully." });
        onSuccess?.("Charge approved successfully!");
        onClose?.();
      } else {
        setStatus({
          type: "error",
          message: data?.message || data?.error || "Failed to approve charge."
        });
      }
    } catch (err) {
      setStatus({
        type: "error",
        message: "Network error. Please try again."
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStatus = () => {
    if (!status.type) return null;
    const base = { padding: "0.5rem", borderRadius: "0.5rem", fontSize: "0.9rem" };
    const styles = {
      info: { ...base, background: "#f1f5f9", border: "1px solid #e2e8f0" },
      success: { ...base, background: "#ecfdf5", border: "1px solid #10b98133" },
      error: { ...base, background: "#fef2f2", border: "1px solid #ef444433" }
    };
    return (
      <div role="status" aria-live="polite" style={styles[status.type]}>
        {status.message}
      </div>
    );
  };

  return (
    <McModal
      ref={modalRef}
      heading="Approve Charge"
      backdropCloseActionDisabled={loading}
      escapeCloseActionDisabled={loading}
      enterCloseActionDisabled={true}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          padding: "1rem"
        }}
        aria-busy={loading ? "true" : "false"}
      >
        <p>
          Confirm approval of charge <b>{childId}</b>?
        </p>

        <McTextarea
          ref={textareaRef}
          label="Comments"
          placeholder="Enter your comments..."
          multiline
          rows={3}
        />

        {renderStatus()}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
          <McButton
            slot="secondaryAction"
            appearance="neutral"
            fit="medium"
            dialogaction="cancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </McButton>
          <McButton
            slot="primaryAction"
            appearance="primary"
            fit="medium"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Confirm"}
          </McButton>
        </div>
      </div>
    </McModal>
  );
}


